import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import type * as type from "@vTypes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import useThrottle from "@/hooks/useThrottle";

interface WeekOffAlertModalProps {
  isOpen: boolean;
  // onClose: () => void;
  portalTargetId?: string;
  currentConfig?: type.ConfigObject;
  isAdmin: boolean;
  selectedGroupId: string | null;
}

export default function WeekOffAlertModal({
  isOpen,
  // onClose,
  portalTargetId = "conf-tab3-container", // 모달을 렌더링할 컨테이너 ID
  currentConfig,
  isAdmin,
  selectedGroupId,
}: WeekOffAlertModalProps) {
  const queryClient = useQueryClient();
  const targetGroupId = isAdmin ? selectedGroupId || undefined : undefined;
  const throttle = useThrottle();

  // 모달 내에서 on/off 상태
  const [weekOffSetting, setWeekOffSetting] = useState(
    currentConfig?.weekly_off_group ?? false
  );

  useEffect(() => {
    if (currentConfig) {
      setWeekOffSetting(currentConfig.weekly_off_group);
    }
  }, [currentConfig]);

  const saveMutation = useMutation({
    mutationFn: async (newConfig: type.ConfigObject) => {
      const base = `${import.meta.env.VITE_API_BASE_URL}/roster/config/save`;
      const url = targetGroupId
        ? `${base}?group_id=${encodeURIComponent(targetGroupId)}`
        : base;

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newConfig),
      });

      if (!response.ok) {
        throw new Error("Save failed");
      }
      return response.json();
    },
    onSuccess: () => {
      alert("설정이 저장되었습니다.");
      // 데이터 갱신 (버전 목록 & 현재 설정)
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configVersions(targetGroupId ?? ""),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configData(targetGroupId ?? ""),
      });
    },
    onError: () => {
      alert("저장에 실패했습니다.");
    },
  });

  // 뒷 화면 스크롤과 텍스트 드래그 막기
  useEffect(() => {
    const targetContainer = document.getElementById(portalTargetId);
    if (targetContainer) {
      if (isOpen) {
        targetContainer.style.setProperty("overflow", "hidden", "important");
        targetContainer.style.setProperty("user-select", "none", "important");
      } else {
        targetContainer.style.removeProperty("overflow");
        targetContainer.style.removeProperty("user-select");
      }
    }
    return () => {
      if (targetContainer) {
        targetContainer.style.removeProperty("overflow");
        targetContainer.style.removeProperty("user-select");
      }
    };
  }, [isOpen, portalTargetId]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="absolute inset-0 z-[200] flex items-start justify-center pt-[50px] mb-[10px]">
      {/* 오버레이 */}
      <div
        className="absolute inset-0 bg-[#FFFFFFCC] bg-opacity-60"
        // onClick={onClose}
      />

      {/* 모달 컨텐츠 */}
      <div
        className="relative bg-white rounded-[10px] w-[273px] px-[52px] py-[30px] shadow-lg border border-[#D6D6DE]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 메시지 */}
        <div className="text-center mb-[9px]">
          <p className="text-[14px] font-pretendard text-black whitespace-nowrap leading-relaxed">
            주휴 설정 OFF 상태입니다.
            <br />
            주휴설정을 ON 하시겠습니까?
          </p>
        </div>

        {/* 주휴 설정 스위치 */}
        <div className="flex justify-center">
          <div className="flex justify-between items-center">
            <div className="text-[#0E59C6] text-[14px] font-pretendard font-bold mr-[6px]">
              주휴 설정
            </div>
            <label className="conf_slideSwitch">
              <input
                type="checkbox"
                checked={weekOffSetting}
                onChange={(e) => {
                  if (!currentConfig) return;
                  setWeekOffSetting(e.currentTarget.checked);
                  throttle(() => {
                    saveMutation.mutate({
                      ...currentConfig,
                      weekly_off_group: e.currentTarget.checked,
                    });
                  });
                }}
              />
              <span className="conf_switchHandler"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const portalRoot = document.getElementById(portalTargetId);
  return portalRoot ? createPortal(modalContent, portalRoot) : modalContent;
}
